import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Package, Plus, Trash2 } from "lucide-react";

const emptyItem = {
  name: "",
  description: "",
  item_code: "",
  hsn_sac: "",
  quantity: 1,
  unit: "NOS",
  rate: 0,
  taxable_value: 0,
  discount_amount: 0,
  net_amount: "",
  gst_rate: 0,
  gross_amount: "",
  total_value: "",
};

const parseNumber = (value, fallback = 0) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeItem = (item = {}) => {
  const quantity = parseNumber(item.quantity, 1);
  const rate = parseNumber(item.rate, 0);
  const taxableValue =
    item.taxable_value !== undefined && item.taxable_value !== ""
      ? parseNumber(item.taxable_value, 0)
      : quantity * rate;
  const discountPercent = parseNumber(item.discount_amount, 0);
  const discountValue = (taxableValue * discountPercent) / 100;
  const explicitNetAmount =
    item.net_amount !== undefined && item.net_amount !== ""
      ? parseNumber(item.net_amount, Number.NaN)
      : Number.NaN;
  const shouldUseExplicitNetAmount =
    Number.isFinite(explicitNetAmount) &&
    !(explicitNetAmount === 0 && (taxableValue > 0 || discountPercent > 0));
  const netAmount =
    shouldUseExplicitNetAmount
      ? explicitNetAmount
      : Math.max(taxableValue - discountValue, 0);
  const gstRate = parseNumber(item.gst_rate, 0);
  const computedTotalValue = netAmount + (netAmount * gstRate) / 100;
  const shouldUseExplicitTotal =
    (item.total_value !== undefined && item.total_value !== "") ||
    (item.gross_amount !== undefined && item.gross_amount !== "") ?
    !(
      parseNumber(item.total_value ?? item.gross_amount, 0) === 0 &&
      (netAmount > 0 || gstRate > 0)
    ) : false;
  const totalValue =
    shouldUseExplicitTotal
      ? parseNumber(item.total_value ?? item.gross_amount, 0)
      : computedTotalValue;

  return {
    ...emptyItem,
    ...item,
    quantity,
    rate,
    taxable_value: taxableValue,
    discount_amount: discountPercent,
    net_amount: netAmount,
    gst_rate: gstRate,
    gross_amount: totalValue,
    total_value: totalValue,
  };
};

export default function InvoiceItemsEditor({
  items = [],
  onChange,
  companyType = "",
  masterItems = [],
}) {
  const safeItems = items.length > 0 ? items.map(normalizeItem) : [normalizeItem()];
  const isProductBased = companyType === "product_based";
  const itemLookups = masterItems.reduce(
    (acc, item = {}) => {
      const nameKey = (item.name || "").trim().toLowerCase();
      const codeKey = (item.item_code || "").trim().toLowerCase();

      if (nameKey && !acc.byName[nameKey]) {
        acc.byName[nameKey] = item;
      }

      if (codeKey && !acc.byCode[codeKey]) {
        acc.byCode[codeKey] = item;
      }

      return acc;
    },
    { byName: {}, byCode: {} },
  );

  const findMasterItemMatch = (item = {}) => {
    const nameKey = (item.name || "").trim().toLowerCase();
    const codeKey = (item.item_code || "").trim().toLowerCase();

    return (
      (nameKey ? itemLookups.byName[nameKey] : null) ||
      (codeKey ? itemLookups.byCode[codeKey] : null) ||
      null
    );
  };

  const getMasterItemRate = (matchedItem = {}) => (
    parseNumber(
      matchedItem.last_sale_price ||
        matchedItem.last_purchase_price ||
        matchedItem.rate ||
        matchedItem.price,
      0,
    )
  );

  const applyMasterItemDetails = (item, matchedItem, options = {}) => {
    const shouldApplyRate = options.applyRate === true;
    const matchedRate = getMasterItemRate(matchedItem);
    const nextItem = {
      ...item,
      name: matchedItem.name || item.name || "",
      item_code: matchedItem.item_code || "",
      description: matchedItem.description || "",
      hsn_sac: matchedItem.hsn_sac || "",
      unit: matchedItem.unit || "NOS",
      gst_rate: matchedItem.gst_rate ?? 0,
      gross_amount: "",
      total_value: "",
    };

    if (shouldApplyRate && matchedRate > 0) {
      nextItem.rate = matchedRate;
      nextItem.taxable_value = "";
      nextItem.net_amount = "";
    }

    return nextItem;
  };

  const updateItem = (index, key, value) => {
    const recalculationKeys = [
      "quantity",
      "rate",
      "taxable_value",
      "discount_amount",
      "gst_rate",
    ];

    const nextItems = safeItems.map((item, currentIndex) => {
      if (currentIndex !== index) {
        return item;
      }

      const nextItem = { ...item, [key]: value };

      if (key === "quantity" || key === "rate") {
        nextItem.taxable_value = "";
      }

      if (recalculationKeys.includes(key)) {
        nextItem.net_amount = "";
        nextItem.gross_amount = "";
        nextItem.total_value = "";
      }

      if (key === "net_amount") {
        nextItem.gross_amount = "";
        nextItem.total_value = "";
      }

      if (key === "gross_amount") {
        nextItem.total_value = value;
      }

      if (key === "total_value") {
        nextItem.gross_amount = value;
      }

      if (key === "name") {
        const matchedItem = itemLookups.byName[(value || "").trim().toLowerCase()];
        if (matchedItem) {
          return normalizeItem(applyMasterItemDetails(nextItem, matchedItem, {
            applyRate: !parseNumber(nextItem.rate, 0),
          }));
        }
      }

      if (key === "item_code") {
        const matchedItem = itemLookups.byCode[(value || "").trim().toLowerCase()];
        if (matchedItem) {
          return normalizeItem(applyMasterItemDetails(nextItem, matchedItem, {
            applyRate: !parseNumber(nextItem.rate, 0),
          }));
        }
      }

      return normalizeItem(nextItem);
    });
    onChange(nextItems);
  };

  const addItem = () => {
    onChange([...safeItems, normalizeItem()]);
  };

  const removeItem = (index) => {
    const nextItems = safeItems.filter((_, currentIndex) => currentIndex !== index);
    onChange(nextItems.length > 0 ? nextItems : [normalizeItem()]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Label className="text-sm font-semibold text-[#0B2B5C]">
              Invoice Items
            </Label>
            <Badge
              variant="outline"
              className={
                isProductBased
                  ? "text-green-700 border-green-200 bg-green-50"
                  : "text-orange-700 border-orange-200 bg-orange-50"
              }
            >
              {isProductBased ? "Inventory Sync On" : "Inventory Sync Off"}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {isProductBased
              ? "Verified purchase and sales invoices will auto-create item master and update stock."
              : "Line items can still be saved, but stock posting stays disabled for service based companies."}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addItem}>
          <Plus size={14} className="mr-1.5" />
          Add Item
        </Button>
      </div>

      <div className="space-y-3">
        {safeItems.map((item, index) => (
          (() => {
            const matchedMasterItem = findMasterItemMatch(item);
            const hasItemIdentity = Boolean(
              (item.name || "").trim() || (item.item_code || "").trim(),
            );
            const statusClass = matchedMasterItem
              ? "border-green-300 bg-green-50/60"
              : hasItemIdentity
                ? "border-red-300 bg-red-50/60"
                : "border-[#0B2B5C]/10 bg-muted/20";
            const statusBadge = matchedMasterItem
              ? { label: "In Item Master", className: "bg-green-100 text-green-700 border-green-200" }
              : hasItemIdentity
                ? { label: "Not In Item Master", className: "bg-red-100 text-red-700 border-red-200" }
                : null;

            return (
          <div
            key={`invoice-item-${index}`}
            className={`rounded-lg border p-4 space-y-3 ${statusClass}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#0B2B5C]">
                <Package size={15} />
                <span className="text-sm font-medium">Item {index + 1}</span>
                {statusBadge && (
                  <Badge variant="outline" className={statusBadge.className}>
                    {statusBadge.label}
                  </Badge>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeItem(index)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 size={14} />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Item Name</Label>
                <Input
                  value={item.name || ""}
                  onChange={(e) => updateItem(index, "name", e.target.value)}
                  className={`mt-1 ${matchedMasterItem ? "border-green-300 focus-visible:ring-green-500" : hasItemIdentity ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                  placeholder="Enter item name"
                  list={`invoice-item-master-${index}`}
                />
                <datalist id={`invoice-item-master-${index}`}>
                  {masterItems.map((masterItem) => (
                    <option key={`${masterItem.id || masterItem.name}-${index}`} value={masterItem.name || ""} />
                  ))}
                </datalist>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input
                  value={item.description || ""}
                  onChange={(e) =>
                    updateItem(index, "description", e.target.value)
                  }
                  className="mt-1"
                  placeholder="Optional description"
                />
              </div>
              <div>
                <Label className="text-xs">Item Code</Label>
                <Input
                  value={item.item_code || ""}
                  onChange={(e) => updateItem(index, "item_code", e.target.value)}
                  className={`mt-1 ${matchedMasterItem ? "border-green-300 focus-visible:ring-green-500" : hasItemIdentity ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                  placeholder="SKU / code"
                />
              </div>
              <div>
                <Label className="text-xs">HSN / SAC</Label>
                <Input
                  value={item.hsn_sac || ""}
                  onChange={(e) => updateItem(index, "hsn_sac", e.target.value)}
                  className="mt-1"
                  placeholder="HSN or SAC"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-xs whitespace-nowrap">Qty</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={item.quantity ?? ""}
                  onChange={(e) => updateItem(index, "quantity", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs whitespace-nowrap">Unit</Label>
                <Input
                  value={item.unit || ""}
                  onChange={(e) => updateItem(index, "unit", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs whitespace-nowrap">Rate</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={item.rate ?? ""}
                  onChange={(e) => updateItem(index, "rate", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs whitespace-nowrap">Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={item.taxable_value ?? ""}
                  onChange={(e) =>
                    updateItem(index, "taxable_value", e.target.value)
                  }
                  className="mt-1"
                />
              </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-xs whitespace-nowrap">Discount %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={item.discount_amount ?? ""}
                  onChange={(e) =>
                    updateItem(index, "discount_amount", e.target.value)
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs whitespace-nowrap">Net Payable</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={item.net_amount ?? ""}
                  onChange={(e) => updateItem(index, "net_amount", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs whitespace-nowrap">GST %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={item.gst_rate ?? ""}
                  onChange={(e) => updateItem(index, "gst_rate", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs whitespace-nowrap">Gross / Total</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={item.total_value ?? ""}
                  onChange={(e) => updateItem(index, "total_value", e.target.value)}
                  className="mt-1"
                />
              </div>
              </div>
            </div>
          </div>
            );
          })()
        ))}
      </div>
    </div>
  );
}
